import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import StandardLayout from '../../../components/layout/StandardLayout';
import { getExams, deleteExam } from '../../../services/examAPIService';
import { PencilIcon, TrashIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useAlert } from '../../../context/AlertContext';

const ExamList = () => {
    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState(null);
    const { showAlert } = useAlert();

    useEffect(() => {
        loadExams();
    }, []);

    const loadExams = async () => {
        try {
            setLoading(true);
            const data = await getExams();
            setExams(data);
            setError(null);
        } catch (err) {
            setError('Fehler beim Laden der Prüfungen');
            console.error('Error loading exams:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (exam) => {
        setDeleteConfirmation(exam);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirmation) return;

        try {
            await deleteExam(deleteConfirmation.id);
            setExams(exams.filter(e => e.id !== deleteConfirmation.id));
            showAlert('Prüfung wurde erfolgreich gelöscht', 'success', true);
            setDeleteConfirmation(null);
        } catch (err) {
            setError('Fehler beim Löschen der Prüfung');
            console.error('Error deleting exam:', err);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmation(null);
    };

    if (loading) {
        return (
            <StandardLayout>
                <div className="flex justify-center items-center min-h-screen">
                    <span className="loading loading-spinner loading-lg text-primary"></span>
                </div>
            </StandardLayout>
        );
    }

    return (
        <StandardLayout>
            <div className="container mx-auto px-4 py-8">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold">Prüfungen</h1>
                    <Link
                        to="/admin/exams/add"
                        className="btn btn-primary"
                    >
                        <PlusIcon className="w-5 h-5 mr-2" />
                        Neue Prüfung
                    </Link>
                </div>

                {error && (
                    <div className="alert alert-error mb-4">
                        <XMarkIcon className="w-6 h-6" />
                        <span>{error}</span>
                    </div>
                )}

                {deleteConfirmation && (
                    <div className="alert alert-warning mb-4">
                        <span>Möchten Sie die Prüfung "{deleteConfirmation.title}" wirklich löschen?</span>
                        <div>
                            <button
                                className="btn btn-sm btn-error mr-2"
                                onClick={handleDeleteConfirm}
                            >
                                Löschen
                            </button>
                            <button
                                className="btn btn-sm"
                                onClick={handleDeleteCancel}
                            >
                                Abbrechen
                            </button>
                        </div>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="table w-full">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Titel</th>
                                <th>Anzahl Prüfungsteile</th>
                                <th className="text-right">Aktionen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {exams.map(exam => (
                                <tr key={exam.id} className="hover">
                                    <td>{exam.id}</td>
                                    <td>{exam.title}</td>
                                    <td>{exam.subcategories?.length || 0}</td>
                                    <td className="text-right">
                                        <div className="flex justify-end gap-2">
                                            <Link
                                                to={`/admin/exams/${exam.id}/edit`}
                                                className="btn btn-sm btn-ghost"
                                                title="Bearbeiten"
                                            >
                                                <PencilIcon className="w-4 h-4" />
                                            </Link>
                                            <button
                                                onClick={() => handleDeleteClick(exam)}
                                                className="btn btn-sm btn-ghost text-error"
                                                title="Löschen"
                                            >
                                                <TrashIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </StandardLayout>
    );
};

export default ExamList; 